import { ColumnDef } from "@tanstack/react-table";
import { BiDotsHorizontalRounded } from "react-icons/bi";

// Helper functions
const enumToStringConverter = (value: string) => {
	if (!value) return "";
	return value
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

const formatDateAndTime = (params: any) => {
	if (!params.value) return "";
	const date = new Date(params.value);
	return date.toLocaleString();
};

export const getOrganizationStatusColor = (status: string) => {
	switch (status) {
		case "ACTIVE":
			return "#4CAF50";
		case "SUSPENDED":
			return "#F44336";
		case "INACTIVE":
			return "#FFC107";
		case "SUSPENDED_PENDING_PAYMENT":
			return "#808080";
		default:
			return "#2196F3";
	}
};

// ==================== ACCOUNT MANAGEMENT COLUMNS ====================
export const accountManagementColumns: ColumnDef<any>[] = [
	{
		accessorKey: "name",
		header: "Name",
		size: 200,
		minSize: 150,
		maxSize: 350,
	},
	{
		accessorKey: "status",
		header: "Status",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const status = getValue() as string;
			const displayText =
				status === "SUSPENDED_PENDING_PAYMENT"
					? "Subscription Expired"
					: enumToStringConverter(status);

			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: getOrganizationStatusColor(status) }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: getOrganizationStatusColor(status),
							opacity: 0.15,
						}}
					/>
					<p>{displayText}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "contactEmail",
		header: "Contact Email",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "contactMsisdn",
		header: "Telephone",
		size: 220,
		minSize: 180,
		maxSize: 300,
	},
	{
		accessorKey: "address",
		header: "Address",
		size: 400,
		minSize: 300,
		maxSize: 600,
	},
	{
		accessorKey: "billingType",
		header: "Billing Type",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => {
			const billingType = getValue() as any;
			return billingType?.name || "--";
		},
	},
	{
		accessorKey: "subsciptionTier",
		header: "Subsciption Tier",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const subscriptionTier = getValue() as any;
			return subscriptionTier?.name || "--";
		},
	},
	{
		accessorKey: "country",
		header: "Country",
		size: 250,
		minSize: 200,
		maxSize: 350,
		cell: ({ getValue }) => {
			const country = getValue() as any;
			return country?.name || "--";
		},
	},
	{
		accessorKey: "currency",
		header: "Currency",
		size: 250,
		minSize: 200,
		maxSize: 350,
		cell: ({ getValue }) => {
			const currency = getValue() as any;
			if (!currency) return "--";
			return `${currency.name} (${currency.code})`;
		},
	},
	{
		id: "maxUsers",
		accessorFn: (row) => row.config?.maxUsers,
		header: "Max Users",
		size: 200,
		minSize: 150,
		maxSize: 300,
	},
	{
		id: "maxCompanies",
		accessorFn: (row) => row.config?.maxCompanies,
		header: "Max Companies",
		size: 200,
		minSize: 150,
		maxSize: 300,
	},
	{
		id: "maxEmployees",
		accessorFn: (row) => row.config?.maxEmployees,
		header: "Max Employees",
		size: 200,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "statusLastModifiedAt",
		header: "Status Date Modified",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 200,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "modifiedAt",
		header: "Date Modified",
		size: 200,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		maxSize: 150,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];

// Default column visibility
export const organizationsColumnVisibility = {
	name: true,
	status: true,
	contactEmail: true,
	contactMsisdn: false,
	address: false,
	billingType: true,
	subsciptionTier: false,
	country: false,
	currency: false,
	maxUsers: false,
	maxCompanies: false,
	maxEmployees: false,
	statusLastModifiedAt: true,
	createdAt: true,
	modifiedAt: true,
	actions: true,
};
